/**
 * TimeWidget — current time + world clock + stopwatch + timer.
 *
 * Compact: large local time, day-of-week, optional 1 favourite city.
 * Expanded: tabs [World] [Stopwatch] [Timer].
 *
 * World clock cities reuse `WatchedCitiesService` (same store as Weather)
 * so adding a favourite city for one surfaces it on the other. Each
 * city's IANA timezone is stored on the WatchedCity record at insertion
 * time (filled from the Open-Meteo geocoding response). Local time is
 * rendered via Intl.DateTimeFormat — zero deps, full IANA tz support
 * built into the browser.
 *
 * No network for the actual clock — just one IP-free fetch when adding a
 * city via search (Open-Meteo geocoding, same endpoint Weather already uses).
 *
 * Tick management: a single module-level setInterval scans the DOM once
 * per second for elements bearing `data-tw-tick="<iana-tz>"` and updates
 * their text content. The interval auto-stops when no such elements remain
 * (e.g. user navigated away). This keeps re-renders cheap and avoids
 * leaking timers across widget unmount/remount cycles.
 */

import type { Widget, WidgetContext, WatchedCity } from '../types';
import { getWatchedCitiesService } from '../../../services/WatchedCitiesService';

// ---------- module-level tick scheduler ----------------------------------

let tickInterval: number | null = null;
let stopwatchInterval: number | null = null;
let timerInterval: number | null = null;

function ensureTick(): void {
  if (tickInterval !== null) return;
  tickInterval = window.setInterval(() => {
    const nodes = document.querySelectorAll<HTMLElement>('[data-tw-tick]');
    if (nodes.length === 0) {
      if (tickInterval !== null) { clearInterval(tickInterval); tickInterval = null; }
      return;
    }
    const now = new Date();
    nodes.forEach((el) => {
      const tz = el.getAttribute('data-tw-tick') || '';
      const fmt = el.getAttribute('data-tw-fmt') || 'time'; // 'time' | 'time_seconds' | 'day' | 'offset'
      try {
        if (fmt === 'time') {
          el.textContent = formatHHMM(now, tz);
        } else if (fmt === 'time_seconds') {
          el.textContent = formatHHMMSS(now, tz);
        } else if (fmt === 'day') {
          el.textContent = formatDayLabel(now, tz, el.getAttribute('data-tw-locale') || 'en');
        } else if (fmt === 'offset') {
          el.textContent = formatOffset(now, tz);
        }
      } catch { /* invalid tz — leave as-is */ }
    });
  }, 1000);
}

// ---------- date helpers --------------------------------------------------

function formatHHMM(d: Date, tz?: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit', minute: '2-digit', hour12: false,
      timeZone: tz || undefined,
    }).format(d);
  } catch {
    return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
  }
}

function formatHHMMSS(d: Date, tz?: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      timeZone: tz || undefined,
    }).format(d);
  } catch {
    return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(d);
  }
}

function formatDayLabel(d: Date, tz: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz || undefined }).format(d);
  } catch {
    return d.toDateString();
  }
}

function formatOffset(d: Date, tz: string): string {
  if (!tz) return '';
  try {
    const fmt = new Intl.DateTimeFormat('en-GB', { timeZone: tz, timeZoneName: 'shortOffset' });
    const parts = fmt.formatToParts(d);
    const part = parts.find((p) => p.type === 'timeZoneName');
    return part?.value || '';
  } catch {
    return '';
  }
}

function isDaytime(d: Date, tz?: string): boolean {
  try {
    const hour = parseInt(new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit', hour12: false, timeZone: tz || undefined,
    }).format(d), 10);
    return hour >= 6 && hour < 19;
  } catch {
    return true;
  }
}

function localTZ(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

// ---------- view-state (per widget render) ------------------------------

type Tab = 'world' | 'stopwatch' | 'timer';
const ACTIVE_TAB_LS_KEY = 'aweni_time_widget_tab';

function loadActiveTab(): Tab {
  try {
    const v = localStorage.getItem(ACTIVE_TAB_LS_KEY);
    if (v === 'stopwatch' || v === 'timer' || v === 'world') return v;
  } catch { /* */ }
  return 'world';
}
function saveActiveTab(t: Tab): void {
  try { localStorage.setItem(ACTIVE_TAB_LS_KEY, t); } catch { /* */ }
}

// ---------- stopwatch helpers --------------------------------------------

interface StopwatchState {
  startedAt: number | null;
  accumulated: number; // ms
  laps: number[];      // ms per lap
}
const SW_LS_KEY = 'aweni_time_widget_sw';
function loadSW(): StopwatchState {
  try {
    const raw = localStorage.getItem(SW_LS_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      if (typeof p?.accumulated === 'number') return p;
    }
  } catch { /* */ }
  return { startedAt: null, accumulated: 0, laps: [] };
}
function saveSW(s: StopwatchState): void {
  try { localStorage.setItem(SW_LS_KEY, JSON.stringify(s)); } catch { /* */ }
}
function swElapsed(s: StopwatchState): number {
  return s.accumulated + (s.startedAt !== null ? Date.now() - s.startedAt : 0);
}
function fmtMs(ms: number, withCs = true): string {
  const totalMs = Math.max(0, Math.floor(ms));
  const cs = Math.floor(totalMs / 10) % 100;
  const total = Math.floor(totalMs / 1000);
  const s = total % 60;
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  return h > 0
    ? `${pad(h)}:${pad(m)}:${pad(s)}${withCs ? `.${pad(cs)}` : ''}`
    : `${pad(m)}:${pad(s)}${withCs ? `.${pad(cs)}` : ''}`;
}

// ---------- timer helpers ------------------------------------------------

interface TimerState {
  endsAt: number | null;     // ms epoch when running
  remaining: number;         // ms when paused
  total: number;             // ms — last started value (for reset/visual)
}
const TIMER_LS_KEY = 'aweni_time_widget_timer';
function loadTimer(): TimerState {
  try {
    const raw = localStorage.getItem(TIMER_LS_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      if (typeof p?.remaining === 'number') return p;
    }
  } catch { /* */ }
  return { endsAt: null, remaining: 0, total: 0 };
}
function saveTimer(s: TimerState): void {
  try { localStorage.setItem(TIMER_LS_KEY, JSON.stringify(s)); } catch { /* */ }
}
function timerLeft(s: TimerState): number {
  if (s.endsAt !== null) return Math.max(0, s.endsAt - Date.now());
  return Math.max(0, s.remaining);
}

// ---------- city add via geocoding ---------------------------------------

interface GeoResult {
  id?: number;
  name: string;
  latitude: number;
  longitude: number;
  country_code?: string;
  admin1?: string;
  timezone?: string;
}

async function searchCity(q: string, lang: string): Promise<GeoResult[]> {
  const query = q.trim();
  if (query.length < 2) return [];
  try {
    const url = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(query)}` +
                `&count=8&language=${encodeURIComponent(lang)}&format=json`;
    const resp = await fetch(url, { cache: 'no-store' });
    if (!resp.ok) return [];
    const json = await resp.json();
    return Array.isArray(json?.results) ? json.results : [];
  } catch {
    return [];
  }
}

// ---------- root render --------------------------------------------------

export const TimeWidget: Widget = {
  id: 'time',
  label: { en: 'Time', fr: 'Heure', ar: 'الوقت', es: 'Hora', de: 'Uhrzeit' },
  icon: '🕐',
  defaultVisible: true,
  defaultOrder: 2,
  defaultSize: 'compact',

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    const tz = localTZ();
    const cities = getWatchedCitiesService().list();
    const t = ctx.t;
    const isRtl = ctx.isRtl;
    const locale = ctx.locale;

    // Inject styles once
    if (!document.getElementById('aweni-tw-styles')) injectStyles();

    if (size === 'compact') {
      const featuredCity = cities.find((c) => c.timeZone) || null;
      container.innerHTML = `
        <button type="button" class="aweni-tw-card aweni-tw-card-compact"
                data-action="open-time" aria-label="${escapeHtml(t('time_open'))}">
          <div class="aweni-tw-row">
            <div class="aweni-tw-local">
              <div class="aweni-tw-time" data-tw-tick="${escapeHtml(tz)}" data-tw-fmt="time">${formatHHMM(new Date(), tz)}</div>
              <div class="aweni-tw-day" data-tw-tick="${escapeHtml(tz)}" data-tw-fmt="day" data-tw-locale="${escapeHtml(locale)}">${formatDayLabel(new Date(), tz, locale)}</div>
            </div>
            ${featuredCity ? renderCompactCity(featuredCity, locale) : ''}
            <span class="aweni-tw-arrow" aria-hidden="true">${isRtl ? '←' : '→'}</span>
          </div>
        </button>
      `;
      container.querySelector('[data-action="open-time"]')?.addEventListener('click', () => {
        // Re-render in expanded mode in place. We swap the container's
        // inner HTML rather than navigating, since the WidgetEngine handles
        // sizing per-widget — but we don't want to mutate the engine's
        // size config on a tap. So we just swap visuals locally.
        TimeWidget.render(container, 'expanded', ctx);
      });
      ensureTick();
      return;
    }

    // Expanded
    const tab = loadActiveTab();
    container.innerHTML = `
      <div class="aweni-tw-card aweni-tw-card-expanded" dir="${isRtl ? 'rtl' : 'ltr'}">
        <div class="aweni-tw-tabs" role="tablist">
          ${tabBtn('world', t('time_tab_world'), tab === 'world')}
          ${tabBtn('stopwatch', t('time_tab_stopwatch'), tab === 'stopwatch')}
          ${tabBtn('timer', t('time_tab_timer'), tab === 'timer')}
          <button type="button" class="aweni-tw-collapse" data-action="collapse" aria-label="${escapeHtml(t('time_collapse'))}">⌃</button>
        </div>
        <div class="aweni-tw-tabpanel" data-panel></div>
      </div>
    `;
    const panel = container.querySelector<HTMLElement>('[data-panel]');
    if (!panel) return;

    container.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const next = btn.getAttribute('data-tab') as Tab;
        saveActiveTab(next);
        // Update tab buttons
        container.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach((b) => {
          b.classList.toggle('is-on', b.getAttribute('data-tab') === next);
          b.setAttribute('aria-selected', String(b.getAttribute('data-tab') === next));
        });
        renderPanel(panel, next, ctx);
      });
    });
    container.querySelector('[data-action="collapse"]')?.addEventListener('click', () => {
      TimeWidget.render(container, 'compact', ctx);
    });

    renderPanel(panel, tab, ctx);
    ensureTick();
  },

  destroy(): void {
    if (tickInterval !== null) { clearInterval(tickInterval); tickInterval = null; }
    if (stopwatchInterval !== null) { clearInterval(stopwatchInterval); stopwatchInterval = null; }
    if (timerInterval !== null) { clearInterval(timerInterval); timerInterval = null; }
  },
};

// ---------- panels -------------------------------------------------------

function renderPanel(panel: HTMLElement, tab: Tab, ctx: WidgetContext): void {
  if (tab === 'world') return renderWorldPanel(panel, ctx);
  if (tab === 'stopwatch') return renderStopwatchPanel(panel, ctx);
  return renderTimerPanel(panel, ctx);
}

function renderWorldPanel(panel: HTMLElement, ctx: WidgetContext): void {
  const t = ctx.t;
  const locale = ctx.locale;
  const tz = localTZ();
  const svc = getWatchedCitiesService();
  const cities = svc.list();

  const list = cities.length === 0
    ? `<li class="aweni-tw-empty">${escapeHtml(t('time_world_empty'))}</li>`
    : cities.map((c) => renderCityRow(c, locale, t)).join('');

  panel.innerHTML = `
    <div class="aweni-tw-local-head">
      <div>
        <div class="aweni-tw-local-time" data-tw-tick="${escapeHtml(tz)}" data-tw-fmt="time_seconds">${formatHHMMSS(new Date(), tz)}</div>
        <div class="aweni-tw-local-meta" data-tw-tick="${escapeHtml(tz)}" data-tw-fmt="day" data-tw-locale="${escapeHtml(locale)}">${formatDayLabel(new Date(), tz, locale)}</div>
        <div class="aweni-tw-local-tz">${escapeHtml(tz)}</div>
      </div>
    </div>

    <ul class="aweni-tw-cities" role="list">${list}</ul>

    <div class="aweni-tw-add">
      <input class="aweni-tw-input" type="text" placeholder="${escapeHtml(t('time_world_add_placeholder'))}"
             data-tw-search autocomplete="off" />
      <ul class="aweni-tw-suggest" data-tw-suggest role="listbox" hidden></ul>
    </div>
  `;

  // City row removal
  panel.querySelectorAll<HTMLButtonElement>('[data-remove-city]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-remove-city');
      if (!id) return;
      svc.remove(id);
      renderWorldPanel(panel, ctx);
    });
  });

  // City search
  const inp = panel.querySelector<HTMLInputElement>('[data-tw-search]');
  const sug = panel.querySelector<HTMLElement>('[data-tw-suggest]');
  if (inp && sug) {
    let timer: number | null = null;
    inp.addEventListener('input', () => {
      if (timer !== null) { clearTimeout(timer); timer = null; }
      const q = inp.value.trim();
      if (q.length < 2) { sug.hidden = true; sug.innerHTML = ''; return; }
      timer = window.setTimeout(async () => {
        const results = await searchCity(q, locale);
        if (results.length === 0) {
          sug.innerHTML = `<li class="aweni-tw-suggest-empty">${escapeHtml(t('time_world_no_match'))}</li>`;
          sug.hidden = false;
          return;
        }
        sug.innerHTML = results.slice(0, 6).map((r, i) => {
          const sub = [r.admin1, r.country_code].filter(Boolean).join(', ');
          return `<li><button type="button" class="aweni-tw-suggest-item" data-add-idx="${i}">
            <span class="aweni-tw-suggest-name">${escapeHtml(r.name)}</span>
            <span class="aweni-tw-suggest-sub">${escapeHtml(sub)}</span>
          </button></li>`;
        }).join('');
        sug.hidden = false;
        sug.querySelectorAll<HTMLButtonElement>('[data-add-idx]').forEach((b) => {
          b.addEventListener('click', () => {
            const idx = parseInt(b.getAttribute('data-add-idx') || '-1', 10);
            const r = results[idx];
            if (!r) return;
            svc.add({
              name: r.name,
              countryCode: r.country_code,
              admin1: r.admin1,
              lat: r.latitude,
              lng: r.longitude,
              timeZone: r.timezone,
            });
            inp.value = '';
            sug.hidden = true; sug.innerHTML = '';
            renderWorldPanel(panel, ctx);
          });
        });
      }, 200);
    });
  }

  ensureTick();
}

function renderCityRow(c: WatchedCity, locale: string, t: WidgetContext['t']): string {
  const tz = c.timeZone || '';
  const time = formatHHMM(new Date(), tz);
  const dayLabel = formatDayLabel(new Date(), tz, locale);
  const offset = formatOffset(new Date(), tz);
  const day = isDaytime(new Date(), tz);
  const sub = [c.admin1, c.countryCode].filter(Boolean).join(', ');
  return `
    <li class="aweni-tw-city">
      <span class="aweni-tw-city-icon" aria-hidden="true">${day ? '☀️' : '🌙'}</span>
      <div class="aweni-tw-city-body">
        <div class="aweni-tw-city-name">${escapeHtml(c.name)}</div>
        <div class="aweni-tw-city-meta">
          <span data-tw-tick="${escapeHtml(tz)}" data-tw-fmt="day" data-tw-locale="${escapeHtml(locale)}">${escapeHtml(dayLabel)}</span>
          ${offset ? ` · <span data-tw-tick="${escapeHtml(tz)}" data-tw-fmt="offset">${escapeHtml(offset)}</span>` : ''}
          ${sub ? ` · ${escapeHtml(sub)}` : ''}
        </div>
      </div>
      <span class="aweni-tw-city-time" data-tw-tick="${escapeHtml(tz)}" data-tw-fmt="time">${escapeHtml(time)}</span>
      <button type="button" class="aweni-tw-city-del" data-remove-city="${escapeHtml(c.id)}"
              aria-label="${escapeHtml(t('time_world_remove'))}">×</button>
    </li>
  `;
}

function renderCompactCity(c: WatchedCity, locale: string): string {
  const tz = c.timeZone || '';
  const time = formatHHMM(new Date(), tz);
  return `
    <div class="aweni-tw-compactcity">
      <div class="aweni-tw-compactcity-name">${escapeHtml(c.name)}</div>
      <div class="aweni-tw-compactcity-time" data-tw-tick="${escapeHtml(tz)}" data-tw-fmt="time">${escapeHtml(time)}</div>
    </div>
  `;
  void locale;
}

// ---------- stopwatch ----------------------------------------------------

function renderStopwatchPanel(panel: HTMLElement, ctx: WidgetContext): void {
  const t = ctx.t;
  let s = loadSW();

  const paint = (): void => {
    const elapsed = swElapsed(s);
    const display = panel.querySelector<HTMLElement>('[data-tw-sw-display]');
    if (display) display.textContent = fmtMs(elapsed);
    const lapsList = panel.querySelector<HTMLElement>('[data-tw-laps]');
    if (lapsList) {
      lapsList.innerHTML = s.laps.length === 0
        ? ''
        : s.laps.map((lap, i) => `<li class="aweni-tw-lap"><span>${escapeHtml(t('time_lap'))} ${s.laps.length - i}</span><span>${fmtMs(lap)}</span></li>`).join('');
    }
    const isRunning = s.startedAt !== null;
    const startBtn = panel.querySelector<HTMLButtonElement>('[data-action="sw-start"]');
    const lapBtn = panel.querySelector<HTMLButtonElement>('[data-action="sw-lap"]');
    const resetBtn = panel.querySelector<HTMLButtonElement>('[data-action="sw-reset"]');
    if (startBtn) startBtn.textContent = isRunning ? t('time_pause') : t('time_start');
    if (lapBtn) lapBtn.disabled = !isRunning;
    if (resetBtn) resetBtn.disabled = isRunning || (s.accumulated === 0 && s.laps.length === 0);
  };

  panel.innerHTML = `
    <div class="aweni-tw-bigtime" data-tw-sw-display>${fmtMs(swElapsed(s))}</div>
    <div class="aweni-tw-actions">
      <button type="button" class="aweni-tw-btn aweni-tw-btn-secondary" data-action="sw-reset">${escapeHtml(t('time_reset'))}</button>
      <button type="button" class="aweni-tw-btn aweni-tw-btn-primary" data-action="sw-start">${escapeHtml(s.startedAt !== null ? t('time_pause') : t('time_start'))}</button>
      <button type="button" class="aweni-tw-btn aweni-tw-btn-secondary" data-action="sw-lap">${escapeHtml(t('time_lap'))}</button>
    </div>
    <ul class="aweni-tw-laps" data-tw-laps role="list"></ul>
  `;

  if (stopwatchInterval !== null) { clearInterval(stopwatchInterval); stopwatchInterval = null; }
  stopwatchInterval = window.setInterval(() => {
    if (s.startedAt !== null) paint();
  }, 50);

  panel.querySelector('[data-action="sw-start"]')?.addEventListener('click', () => {
    if (s.startedAt !== null) {
      s.accumulated += Date.now() - s.startedAt;
      s.startedAt = null;
    } else {
      s.startedAt = Date.now();
    }
    saveSW(s);
    paint();
  });
  panel.querySelector('[data-action="sw-lap"]')?.addEventListener('click', () => {
    if (s.startedAt === null) return;
    s.laps.unshift(swElapsed(s));
    if (s.laps.length > 50) s.laps.length = 50;
    saveSW(s);
    paint();
  });
  panel.querySelector('[data-action="sw-reset"]')?.addEventListener('click', () => {
    if (s.startedAt !== null) return;
    s = { startedAt: null, accumulated: 0, laps: [] };
    saveSW(s);
    paint();
  });

  paint();
}

// ---------- timer --------------------------------------------------------

const TIMER_PRESETS_MIN = [1, 5, 10, 15, 25];

function renderTimerPanel(panel: HTMLElement, ctx: WidgetContext): void {
  const t = ctx.t;
  let s = loadTimer();

  const paint = (): void => {
    const left = timerLeft(s);
    const display = panel.querySelector<HTMLElement>('[data-tw-timer-display]');
    if (display) display.textContent = fmtMs(left, false);
    const pct = s.total > 0 ? Math.max(0, Math.min(100, (left / s.total) * 100)) : 0;
    const bar = panel.querySelector<HTMLElement>('[data-tw-timer-bar]');
    if (bar) bar.style.width = `${pct}%`;
    const isRunning = s.endsAt !== null;
    const startBtn = panel.querySelector<HTMLButtonElement>('[data-action="t-start"]');
    if (startBtn) {
      startBtn.textContent = isRunning ? t('time_pause') : t('time_start');
      startBtn.disabled = !isRunning && s.remaining === 0 && s.total === 0;
    }

    if (isRunning && left === 0) {
      // Time's up
      s = { endsAt: null, remaining: 0, total: s.total };
      saveTimer(s);
      onTimerComplete(ctx);
      paint();
    }
  };

  const presetsHtml = TIMER_PRESETS_MIN.map((m) =>
    `<button type="button" class="aweni-tw-preset" data-timer-preset="${m}">${m}m</button>`,
  ).join('');

  panel.innerHTML = `
    <div class="aweni-tw-bigtime" data-tw-timer-display>${fmtMs(timerLeft(s), false)}</div>
    <div class="aweni-tw-progress"><div class="aweni-tw-progress-bar" data-tw-timer-bar></div></div>
    <div class="aweni-tw-presets">
      ${presetsHtml}
      <button type="button" class="aweni-tw-preset" data-action="t-custom">${escapeHtml(t('time_custom'))}</button>
    </div>
    <div class="aweni-tw-actions">
      <button type="button" class="aweni-tw-btn aweni-tw-btn-secondary" data-action="t-reset">${escapeHtml(t('time_reset'))}</button>
      <button type="button" class="aweni-tw-btn aweni-tw-btn-primary" data-action="t-start">${escapeHtml(s.endsAt !== null ? t('time_pause') : t('time_start'))}</button>
    </div>
  `;

  if (timerInterval !== null) { clearInterval(timerInterval); timerInterval = null; }
  timerInterval = window.setInterval(() => paint(), 200);

  panel.querySelectorAll<HTMLButtonElement>('[data-timer-preset]').forEach((b) => {
    b.addEventListener('click', () => {
      const m = parseInt(b.getAttribute('data-timer-preset') || '0', 10);
      if (!Number.isFinite(m) || m <= 0) return;
      const ms = m * 60 * 1000;
      s = { endsAt: null, remaining: ms, total: ms };
      saveTimer(s);
      paint();
    });
  });
  panel.querySelector('[data-action="t-custom"]')?.addEventListener('click', () => {
    const raw = window.prompt(t('time_custom_prompt'), '20');
    if (!raw) return;
    const m = parseFloat(raw);
    if (!Number.isFinite(m) || m <= 0) return;
    const ms = Math.round(m * 60 * 1000);
    s = { endsAt: null, remaining: ms, total: ms };
    saveTimer(s);
    paint();
  });
  panel.querySelector('[data-action="t-start"]')?.addEventListener('click', () => {
    if (s.endsAt !== null) {
      s.remaining = Math.max(0, s.endsAt - Date.now());
      s.endsAt = null;
    } else if (s.remaining > 0) {
      s.endsAt = Date.now() + s.remaining;
      s.remaining = 0;
    }
    saveTimer(s);
    paint();
  });
  panel.querySelector('[data-action="t-reset"]')?.addEventListener('click', () => {
    s = { endsAt: null, remaining: s.total, total: s.total };
    saveTimer(s);
    paint();
  });

  paint();
}

function onTimerComplete(ctx: WidgetContext): void {
  ctx.showNotification(ctx.t('time_timer_done'), 'success');
  // Best-effort vibration on mobile (no permission needed for short bursts).
  try { (navigator as Navigator & { vibrate?: (p: number | number[]) => void }).vibrate?.([200, 100, 200, 100, 400]); } catch { /* */ }
  // Audible cue (Web Audio API — silent if no user gesture yet).
  try {
    const AC = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext
              || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (AC) {
      const ac = new AC();
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.frequency.value = 880;
      o.connect(g); g.connect(ac.destination);
      g.gain.setValueAtTime(0.0001, ac.currentTime);
      g.gain.exponentialRampToValueAtTime(0.18, ac.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + 0.6);
      o.start();
      o.stop(ac.currentTime + 0.62);
      o.onended = () => { try { ac.close(); } catch { /* */ } };
    }
  } catch { /* audio blocked */ }
}

// ---------- DOM helpers --------------------------------------------------

function tabBtn(tab: Tab, label: string, on: boolean): string {
  return `<button type="button" class="aweni-tw-tab ${on ? 'is-on' : ''}" role="tab"
                  data-tab="${tab}" aria-selected="${on}">${escapeHtml(label)}</button>`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c));
}

// ---------- styles -------------------------------------------------------

function injectStyles(): void {
  const style = document.createElement('style');
  style.id = 'aweni-tw-styles';
  style.textContent = `
    .aweni-tw-card {
      display: block; width: 100%;
      padding: 16px;
      border-radius: 16px;
      background: var(--ln-bg-card, var(--card-bg, #FFFFFF));
      box-shadow: var(--ln-shadow-sm, var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.06)));
      color: var(--ln-text-primary, var(--text-primary, #1E293B));
      border: 1px solid var(--ln-border, var(--border, #E5E7EB));
      text-align: start;
      font: inherit;
      cursor: pointer;
      transition: border-color var(--motion-duration-fast) var(--motion-easing-default);
    }
    button.aweni-tw-card { cursor: pointer; }
    .aweni-tw-card:hover { border-color: var(--ln-accent, #0A0A0A); }
    .aweni-tw-card-expanded { cursor: default; }

    .aweni-tw-row { display: flex; align-items: center; gap: 14px; }
    .aweni-tw-local { flex: 1; min-width: 0; }
    .aweni-tw-time {
      font-size: 30px; font-weight: 800;
      letter-spacing: -0.5px; line-height: 1;
      font-variant-numeric: tabular-nums;
    }
    .aweni-tw-day {
      margin-top: 4px;
      font-size: 12px; color: var(--ln-text-secondary, #64748B);
    }
    .aweni-tw-arrow {
      font-size: 18px;
      color: var(--ln-text-secondary, #64748B);
      flex-shrink: 0;
    }
    .aweni-tw-compactcity {
      flex: 1; min-width: 0; max-width: 130px;
      padding-inline: 12px;
      border-inline-start: 1px solid var(--ln-border, #E5E7EB);
    }
    .aweni-tw-compactcity-name {
      font-size: 12px; font-weight: 600;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .aweni-tw-compactcity-time {
      margin-top: 2px;
      font-size: 16px; font-weight: 700;
      font-variant-numeric: tabular-nums;
      color: var(--ln-text-secondary, #64748B);
    }

    /* Tabs */
    .aweni-tw-tabs {
      display: flex; gap: 4px;
      padding: 4px;
      background: var(--ln-bg-secondary, rgba(0,0,0,0.04));
      border-radius: 12px;
      margin-bottom: 14px;
    }
    .aweni-tw-tab {
      flex: 1;
      padding: 8px 10px;
      background: transparent; border: none;
      border-radius: 8px;
      font-size: 13px; font-weight: 600;
      color: var(--ln-text-primary, var(--text-primary, #1E293B));
      cursor: pointer;
      transition: background var(--motion-duration-fast) var(--motion-easing-default);
    }
    .aweni-tw-tab.is-on {
      background: var(--ln-bg-card, #FFFFFF);
      box-shadow: 0 1px 3px rgba(0,0,0,0.08);
    }
    .aweni-tw-collapse {
      flex-shrink: 0; width: 32px;
      background: transparent; border: none;
      border-radius: 8px; cursor: pointer;
      font-size: 16px; font-weight: 700;
      color: var(--ln-text-secondary, #64748B);
    }
    .aweni-tw-collapse:hover { background: var(--ln-bg-card, rgba(255,255,255,0.6)); }

    /* World panel */
    .aweni-tw-local-head {
      padding-bottom: 12px;
      margin-bottom: 12px;
      border-bottom: 1px solid var(--ln-border, var(--border, #E5E7EB));
    }
    .aweni-tw-local-time {
      font-size: 36px; font-weight: 800;
      font-variant-numeric: tabular-nums;
      letter-spacing: -0.5px;
      line-height: 1;
    }
    .aweni-tw-local-meta {
      margin-top: 4px;
      font-size: 13px;
      color: var(--ln-text-secondary, #64748B);
    }
    .aweni-tw-local-tz {
      font-size: 11px;
      color: var(--ln-text-secondary, #94A3B8);
      margin-top: 2px;
    }
    .aweni-tw-cities {
      list-style: none; padding: 0; margin: 0;
      display: flex; flex-direction: column; gap: 6px;
    }
    .aweni-tw-empty {
      padding: 14px;
      text-align: center;
      font-size: 13px;
      color: var(--ln-text-secondary, #64748B);
    }
    .aweni-tw-city {
      display: flex; align-items: center; gap: 10px;
      padding: 10px 12px;
      background: var(--ln-bg-secondary, rgba(0,0,0,0.03));
      border-radius: 12px;
    }
    .aweni-tw-city-icon { font-size: 16px; }
    .aweni-tw-city-body { flex: 1; min-width: 0; }
    .aweni-tw-city-name {
      font-weight: 600; font-size: 14px;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .aweni-tw-city-meta {
      margin-top: 2px;
      font-size: 11px;
      color: var(--ln-text-secondary, #64748B);
    }
    .aweni-tw-city-time {
      font-variant-numeric: tabular-nums;
      font-size: 17px; font-weight: 700;
    }
    .aweni-tw-city-del {
      flex-shrink: 0;
      width: 26px; height: 26px;
      border: none; background: transparent;
      border-radius: 6px;
      font-size: 18px; color: var(--ln-text-secondary, #64748B);
      cursor: pointer;
    }
    .aweni-tw-city-del:hover { background: rgba(220, 38, 38, 0.08); color: #DC2626; }

    .aweni-tw-add { position: relative; margin-top: 12px; }
    .aweni-tw-input {
      width: 100%;
      padding: 10px 12px;
      border: 1px solid var(--ln-border, var(--border, #E5E7EB));
      border-radius: 10px;
      background: var(--ln-bg-card, #FFFFFF);
      color: inherit;
      font-size: 14px;
      outline: none;
    }
    .aweni-tw-input:focus { border-color: var(--ln-accent, #0A0A0A); }
    .aweni-tw-suggest {
      position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 5;
      list-style: none; padding: 0; margin: 0;
      background: var(--ln-bg-card, #FFFFFF);
      border: 1px solid var(--ln-border, #E5E7EB);
      border-radius: 12px;
      box-shadow: 0 6px 16px rgba(0,0,0,0.12);
      max-height: 240px; overflow-y: auto;
    }
    .aweni-tw-suggest-item {
      width: 100%; text-align: start;
      padding: 8px 12px;
      background: transparent; border: none;
      cursor: pointer; font: inherit;
      display: flex; flex-direction: column; gap: 2px;
    }
    .aweni-tw-suggest-item:hover { background: var(--ln-bg-secondary, rgba(0,0,0,0.04)); }
    .aweni-tw-suggest-name { font-weight: 600; font-size: 13px; }
    .aweni-tw-suggest-sub { font-size: 11px; color: var(--ln-text-secondary, #64748B); }
    .aweni-tw-suggest-empty { padding: 10px 12px; font-size: 13px; color: var(--ln-text-secondary, #64748B); }

    /* Stopwatch + timer common */
    .aweni-tw-bigtime {
      font-size: 48px; font-weight: 800;
      font-variant-numeric: tabular-nums;
      letter-spacing: -1px;
      text-align: center;
      padding: 12px 0;
    }
    .aweni-tw-actions {
      display: flex; gap: 8px;
      margin-top: 12px;
    }
    .aweni-tw-btn {
      flex: 1;
      min-height: 44px;
      padding: 10px 14px;
      border: none; border-radius: 12px;
      font-size: 15px; font-weight: 700;
      cursor: pointer;
      transition: opacity var(--motion-duration-fast) var(--motion-easing-default), transform var(--motion-duration-fast) var(--motion-easing-default);
    }
    .aweni-tw-btn:disabled { opacity: 0.4; cursor: not-allowed; }
    .aweni-tw-btn-primary {
      background: var(--ln-accent, #0A0A0A);
      color: var(--ln-accent-text, #fff);
    }
    .aweni-tw-btn-secondary {
      background: var(--ln-bg-secondary, rgba(0,0,0,0.06));
      color: var(--ln-text-primary, var(--text-primary, #1E293B));
    }
    .aweni-tw-btn:active:not(:disabled) { transform: scale(0.98); }

    .aweni-tw-laps {
      list-style: none; padding: 0; margin: 12px 0 0;
      display: flex; flex-direction: column; gap: 4px;
      max-height: 180px; overflow-y: auto;
    }
    .aweni-tw-lap {
      display: flex; justify-content: space-between;
      padding: 6px 12px;
      background: var(--ln-bg-secondary, rgba(0,0,0,0.03));
      border-radius: 8px;
      font-size: 13px;
      font-variant-numeric: tabular-nums;
    }

    .aweni-tw-progress {
      width: 100%; height: 6px;
      background: var(--ln-bg-secondary, rgba(0,0,0,0.08));
      border-radius: 9999px; overflow: hidden;
      margin-bottom: 12px;
    }
    .aweni-tw-progress-bar {
      height: 100%;
      background: var(--ln-accent, #0A0A0A);
      transition: width var(--motion-duration-base) linear;
    }
    .aweni-tw-presets {
      display: flex; gap: 6px; flex-wrap: wrap;
      margin-bottom: 4px;
    }
    .aweni-tw-preset {
      padding: 8px 12px;
      background: var(--ln-bg-secondary, rgba(0,0,0,0.04));
      color: inherit;
      border: 1px solid transparent;
      border-radius: 9999px;
      font-size: 13px; font-weight: 600;
      cursor: pointer;
    }
    .aweni-tw-preset:hover { border-color: var(--ln-accent, #0A0A0A); }
  `;
  document.head.appendChild(style);
}
